import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import Modal from "../../utils/components/common/modal";
import Icons from "../../utils/components/icons";
import DropDown from "../../utils/components/common/dropdown/dropdown";
import { UserContext } from "../../layouts/MainLayout";
import { useTranslation } from "react-i18next";
import "./editUser.scss";
import ModalComponent from "../../utils/components/common/modalComponent/ModalComponent";

const EditUser = ({
  selectedUser,
  allSitesList,
  handleDisableConfirmation,
  handleDeleteConfirmation,
  handleEditUser,
  onClose,
}) => {
  const { t } = useTranslation("usermanagement");

  const { userData } = useContext(UserContext);

  const rolesForAdmin = [
    { label: t("roles.user"), value: "User" },
    { label: t("roles.viewOnlyUser"), value: "View Only User" },
  ];

  const rolesForSuperAdmin = [
    { label: t("roles.user"), value: "User" },
    { label: t("roles.admin"), value: "Admin" },
    {
      label: t("roles.superAdmin"),
      value: "Super Admin",
    },
    { label: t("roles.viewOnlyUser"), value: "View Only User" },
  ];

  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [showDisableConfirmModal, setShowDisableConfirmModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);

  const [selectedUserRole, setSelectedUserRole] = useState(
    selectedUser.UserRole
  );
  const [selectedUserSites, setSelectedUserSites] = useState([]);
  const [loggedInUserSites, setLoggedInUserSites] = useState([]); // To check the site access of logged in user.

  const getSiteListForUser = useCallback(
    (user) => {
      // if(!user?.SiteList) return;

      if (user?.SiteList && !user.SiteList.length) return []; //No sites available case;

      const siteIdList = allSitesList.map((site) => site.SiteId);

      if (user?.SiteList[0] === "All Sites") {
        return siteIdList;
      }

      const siteList = siteIdList.filter((siteId) => {
        return user.SiteList.includes(siteId);
      });
      return siteList;
    },
    [allSitesList]
  );

  const previousSelectedSites = useMemo(() => {
    return getSiteListForUser(selectedUser);
  }, [getSiteListForUser, selectedUser]);

  /**If selected role is Super Admin then select all sites. */
  const sitesForSA = useMemo(() => {
    return allSitesList.map((site) => site.SiteId);
  }, [allSitesList]);

  /**To update selected sites for selectedUser and loggedInUser  */
  useEffect(() => {
    const selectedUserSiteList = getSiteListForUser(selectedUser);
    const loggedInUserSiteList = getSiteListForUser(userData);

    setSelectedUserSites([...selectedUserSiteList]);
    setLoggedInUserSites([...loggedInUserSiteList]);
  }, [selectedUser, allSitesList, getSiteListForUser, userData]);

  const isUserDisable = useMemo(() => {
    return selectedUser.AccountStatus === "Disabled";
  }, [selectedUser]);

  const handleEditRoles = useCallback((userRole) => {
    setSelectedUserRole(userRole);
  }, []);

  /**On Disable user confirm */
  const onEnableStatusChange = () => {
    const action =
      selectedUser.AccountStatus === "Disabled" ? "enable" : "disable";
    setShowDisableConfirmModal(false);
    handleDisableConfirmation(selectedUser, action);
  };

  const onSiteSelect = useCallback(
    (evt) => {
      const selectedSiteId = evt.target.value;
      if (evt.target.checked) {
        setSelectedUserSites((prevVal) => {
          const selectedSite = allSitesList.find(
            (site) => site.SiteId === selectedSiteId
          );
          return [...prevVal, selectedSite.SiteId];
        });
      } else {
        setSelectedUserSites((prevVal) => {
          const list = prevVal.filter((site) => site !== selectedSiteId);
          return [...list];
        });
      }
    },
    [allSitesList]
  );

  const onSaveConfirmation = useCallback(() => {
    setShowSaveConfirmModal(false);
    if (selectedUserRole === "Super Admin") {
      handleEditUser(selectedUserRole, sitesForSA);
    } else {
      handleEditUser(selectedUserRole, selectedUserSites);
    }
  }, [handleEditUser, selectedUserRole, sitesForSA, selectedUserSites]);

  const isSaveEnable = useMemo(() => {
    if (!selectedUserSites?.length) return false;

    /**If role change then enable save button */
    if (selectedUser.UserRole !== selectedUserRole) return true;

    if (previousSelectedSites.length !== selectedUserSites.length) return true;

    /**Check previous sites in new sites array, if element mismatch then enable save button */
    const siteCheck = previousSelectedSites.every((e) => {
      if (!selectedUserSites.find((e1) => e1 === e)) {
        return false;
      }
      return true;
    });

    return !siteCheck;
  }, [
    selectedUser.UserRole,
    selectedUserRole,
    previousSelectedSites,
    selectedUserSites,
  ]);

  const isSiteChecked = useCallback((siteList, currentSite) => {
    const sites = siteList.find((siteId) => siteId === currentSite);
    return !!sites;
  }, []);

  return (
    <>
      {/* Edit User Modal Starts */}
      <div className={"editUserContainer"}>
        <Modal
          idName="editUserMainModal"
          isOpen
          onCloseModal={() => setShowCancelConfirmModal(true)}
          className=""
        >
          <div>
            <span className="" id="edit_user">
              Edit User
            </span>
          </div>
          <div className="edit-user-wrapper">
            <div className="upperSection">
              <div className=" upperSectionEdit d-flex">
                <div className="leftUpperSectionEdit">
                  {Icons.editEmailIcon}
                  <span id="selectedUserEmail">{selectedUser.Email}</span>
                </div>

                <div className="righttUpperSectionEdit">
                  {isUserDisable ? (
                    <button
                      id="enable_user"
                      className="verticalBtn"
                      onClick={() => setShowDisableConfirmModal(true)}
                    >
                      {" "}
                      {Icons.editUserIcon}Enable User
                    </button>
                  ) : (
                    <button
                      id="disable_user"
                      className="verticalBtn"
                      onClick={() => setShowDisableConfirmModal(true)}
                    >
                      {" "}
                      {Icons.editUserIcon}Disable User
                    </button>
                  )}
                  <button
                    className="verticalBtn"
                    id="delete_user_btn"
                    onClick={() => {
                      setShowDeleteConfirmModal(true);
                    }}
                    // disabled={selectedUserSites?.length}
                    disabled={true}
                  >
                    {Icons.deleteUSerIcon}
                    {/* Delete User */}
                    <span id="delete_user">{t("deleteUser")}</span>
                  </button>
                </div>
              </div>
              <div className="d-flex justify-content-between gap-4 align-items-center">
                <div className="detailsContainer">
                  <span className="d-flex gap-2">
                    {Icons.editNameIcon}
                    <span id="usernameDetail">{selectedUser.UserName}</span>
                  </span>
                  <span className="d-flex gap-2">
                    {Icons.smartphoneIcon}
                    <span id="phoneNoDetail">{selectedUser.PhoneNumber}</span>
                  </span>
                </div>

                <div className="editUserDropDownContainers">
                  {userData ? (
                    <DropDown
                      disabled={isUserDisable}
                      options={
                        userData.UserRole === "Admin"
                          ? rolesForAdmin
                          : rolesForSuperAdmin
                      }
                      wrapperClass={isUserDisable ? "dark" : "primary"}
                      autoid="edit_roles_list"
                      value={selectedUserRole}
                      onSelection={handleEditRoles}
                    />
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </div>
            <hr />
            <div className="bottomSection">
              <div className="siteDetailsContainer">
                {Icons.sitesICon}
                <span id="sites_count">Sites : {allSitesList?.length}</span>
              </div>

              <div className="checkboxContainer edit" id="site_select">
                {allSitesList.map((sites, index) => (
                  <label key={`${index + 1}`}>
                    <span
                      id={`siteName_${index + 1}`}
                      className={
                        isSiteChecked(selectedUserSites, sites.SiteId)
                          ? "selected"
                          : ""
                      }
                    >
                      {sites.SiteName}
                    </span>
                    <input
                      type="checkbox"
                      id="siteCheckbox"
                      value={sites.SiteId}
                      onClick={(evt) => onSiteSelect(evt)}
                      onChange={(e) => {}}
                      disabled={
                        isUserDisable ||
                        selectedUserRole === "Super Admin" ||
                        !loggedInUserSites.find(
                          (siteId) => siteId === sites.SiteId
                        )
                      }
                      title={
                        loggedInUserSites.find(
                          (siteId) => siteId === sites.SiteId
                        )
                          ? " "
                          : t("disabled")
                      }
                      checked={
                        selectedUserRole === "Super Admin"
                          ? isSiteChecked(sitesForSA, sites.SiteId)
                          : isSiteChecked(selectedUserSites, sites.SiteId)
                      }
                    />
                    <span
                      className="checkbox-icon-wrapper"
                      id={`checkbox-icon-wrapper-${index + 1}`}
                    >
                      <span
                        className="checkbox-icon"
                        id={`checkbox-icon-${index + 1}`}
                      >
                        {!isUserDisable &&
                        // selectedUserRole !== "Super Admin" &&
                        loggedInUserSites.find(
                          (siteId) => siteId === sites.SiteId
                        ) ? (
                          <>{Icons.purpleTickCheckedBox}</>
                        ) : (
                          <>{Icons.disabledGrayedTickCheckedBox}</>
                        )}
                      </span>
                    </span>
                  </label>
                ))}
              </div>

              <div className="buttonContainer">
                <button
                  type="button"
                  id="cancel"
                  className="btn btn-secondary edit"
                  onClick={() => {
                    setShowCancelConfirmModal(true);
                  }}
                >
                  {t("cancel")}
                </button>
                <button
                  type="submit"
                  id="save"
                  className="roundBtn btn-primary edit"
                  disabled={isUserDisable || !isSaveEnable}
                  onClick={() => setShowSaveConfirmModal(true)}
                >
                  {t("save")}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </div>
      {/* Edit User Modal Ends */}

      {/* CancelConfirmationModal:- On click of Cancel button Starts */}

      <ModalComponent
        idName="CancelConfirmationModal"
        isOpen={showCancelConfirmModal}
        onCloseModal={() => setShowSaveConfirmModal(false)}
        descriptionText={t("cancel_warning_message")}
        needConfirmation={true}
        onConfirm={() => {
          setShowCancelConfirmModal(false);
          onClose();
        }}
        onClose={() => setShowCancelConfirmModal(false)}
      ></ModalComponent>

      {/* CancelConfirmationModal:- On click of Cancel button Ends */}

      {/* SaveConfirmationModal:- On click of Save button Starts */}

      <ModalComponent
        idName="editSaveUsermodal"
        isOpen={showSaveConfirmModal}
        onCloseModal={() => setShowSaveConfirmModal(false)}
        descriptionText={t("save_confirmation_message")}
        needConfirmation={true}
        onConfirm={() => onSaveConfirmation()}
        onClose={() => setShowSaveConfirmModal(false)}
      ></ModalComponent>

      {/* SaveConfirmationModal:- On click of Save button Ends */}

      {/* DisableConfirmationModal:- On click of Disable button Starts */}
      {showDisableConfirmModal ? (
        <ModalComponent
          idName="disableUsermodal"
          isOpen={showDisableConfirmModal}
          onCloseModal={() => setShowDisableConfirmModal(false)}
          descriptionText={t("disable_warning_message", {
            userStatus: isUserDisable ? "Enable" : "Disable",
          })}
          needConfirmation={true}
          onConfirm={onEnableStatusChange}
          onClose={() => setShowDisableConfirmModal(false)}
        ></ModalComponent>
      ) : (
        <></>
      )}
      {/* DisableConfirmationModal:- On click of Disable button Ends */}

      {/* Not being used */}
      {/* DeleteConfirmationModal:- On click of Delete button Starts */}
      <ModalComponent
        idName="deleteConfirmationModal"
        isOpen={showDeleteConfirmModal}
        onCloseModal={() => setShowDeleteConfirmModal(false)}
        descriptionText={t("confirm_delete_user")}
        needConfirmation={true}
        onConfirm={() => {
          handleDeleteConfirmation(selectedUser);
        }}
        onClose={() => {
          setShowDeleteConfirmModal(false);
        }}
      ></ModalComponent>

      {/* DeleteConfirmationModal:- On click of Delete button Ends */}
    </>
  );
};

export default EditUser;